import React from 'react';
import { AlertTriangle, Users, PiggyBank, Clock, MonitorPlay } from 'lucide-react';

import { ReactComponent as MonitorIcon } from '../assets/icons/monitor.svg';
import { ReactComponent as UsersIcon } from '../assets/icons/users.svg';
import { ReactComponent as PiggyBankIcon } from '../assets/icons/piggy.svg';
import { ReactComponent as WarningIcon } from '../assets/icons/warning.svg';
import { ReactComponent as ClockIcon } from '../assets/icons/clock.svg';
import { ReactComponent as MessageIcon } from '../assets/icons/message.svg';

const FeatureCard = ({ icon: Icon, title, description, iconColor }) => {
    return (
        <div className="group relative flex flex-col items-center transform transition-transform duration-300 hover:-translate-y-2">
            {/* Hover Effect Background */}
            <div className="absolute inset-0 bg-[#2A2E35] opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-300 -z-10 shadow-lg"></div>

            {/* Icon Container */}
            <div className="relative">
                <div className="flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                    <div className={`${iconColor}`}>
                        <Icon/>
                    </div>
                </div>
            </div>

            {/* Text Content */}
            <div className="mt-6 md:mt-8 text-center p-3 md:p-4 transition-all duration-300 group-hover:translate-y-1">
                <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3 text-white">
                    {title}
                </h3>
                <p className="text-sm md:text-base text-gray-400 leading-relaxed transition-colors duration-300 group-hover:text-gray-300">
                    {description}
                </p>
            </div>

            {/* Hover Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg opacity-0 group-hover:opacity-10 blur transition duration-300"></div>
        </div>
    );
};

const FeatureGrid = () => {
    const features = [
        {
            icon: MonitorIcon,
            title: "БҮРЭН ЦАХИМЖИЛТ",
            description: "Төсвийн захирагчийн төсөв боловсруулах, хуваарилах, гүйцэтгэх цаасан суурьтай ажиллагаа бүрэн цахимжиж. Төсвийн гүйцэтгэлээ тухай бүр харах, хянах бололмжтой боллоо.",
            iconColor: "text-yellow-400"
        },
        {
            icon: UsersIcon,
            title: "НЭГ ТОО, НЭГ МЭДЭЭЛЭЛ",
            description: "Байгууллага өөрийн үйл ажиллагаатай холбоотой хууль эрх зүй, төсөв болон статистик, салбарын хамгийн сүүлийн үеийн мэдээ мэдээллийг харах боломжийг бүрдүүлснээр нэг тоо, нэг мэдээлэл дээр ажиллах боломжтой болсон.",
            iconColor: "text-orange-400"
        },
        {
            icon: PiggyBankIcon,
            title: "ИХ ӨГӨГДӨЛ",
            description: "Яам түүний харъяа агентлаг, байгууллагуудын чиг үүрэг, албан хаагчид, хууль эрх зүй, статистик болон бүх цаг үеийн түүхэн төсвийн мэдээллүүд нэгтгэгдлээ.",
            iconColor: "text-red-400"
        },
        {
            icon: WarningIcon,
            title: "ТӨСВИЙН ТООЦООЛОЛ",
            description: "Улсын хэмжээнд төсвийн тооцооллын стандарт бүхий маягтыг боловсруулан орлого, зарлагын анхан шатны тооцоолол хийх бололцоог бий болгосон.​​.",
            iconColor: "text-yellow-400"
        },
        {
            icon: ClockIcon,
            title: "ТӨСӨВ БОЛОВСРУУЛАЛТ",
            description: "Өнгөрсөн мэдээлэл дээр дүн шинжилгээ хийж ТШЗ, ТТЗ, ТЕЗ-ийн шатлал хооронд төсвийн төслийг боловсруулах, хянах, баталгаажуулах боломжтой болсон. ​​  ​",
            iconColor: "text-orange-400"
        },
        {
            icon: MessageIcon,
            title: "ТӨСВИЙН ХУВААРЬ",
            description: "Хуваарийн санал боловсруулах болон сар бүрийн хуваарийн өөрчлөлт хийгддэг байдлыг цахимжуулж, салбарын хэмжээнд хаана ямар төсөв илүү, дутуу гарч байгааг нэгтгэж харах, хянах, удирдан зохион байгуулах боломжтой болсон.​",
            iconColor: "text-red-400"
        }
    ];

    return (
        <div
            className="min-h-screen min-w-screen h-full flex flex-col md:flex-row items-center px-4 md:px-24 py-8 md:py-16 overflow-auto">
            <div className="md:hidden mb-8">
                <h2 className="text-3xl font-bold text-center">
                    ДАВУУ ТАЛ
                </h2>
            </div>

            <div className="relative flex flex-1">
                {/* Centered vertical header - hidden on mobile */}
                <div className="hidden md:block absolute top-1/2 -translate-y-1/2">
                    <h2 className="text-4xl font-bold transform -rotate-90 whitespace-nowrap origin-center">
                        ДАВУУ ТАЛ
                    </h2>
                </div>

                {/* Feature cards grid */}
                <div className="flex-1 md:ml-40"> {/* Space for the rotated header on desktop */}
                    <div className="flex items-center justify-center min-h-full">
                        <div
                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 lg:gap-16 max-w-6xl mx-auto">
                            {features.map((feature, index) => (
                                <FeatureCard
                                    key={index}
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                    iconColor={feature.iconColor}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeatureGrid;